import React, { useState } from "react";
import { API } from "aws-amplify";
import {
  findExistingUserByEmail,
  performCreateUserReloadCarrier,
  performUpdateUserReloadCarrier,
  sanitizeCarrierUser,
} from "../../../../graphql/carrier/carrierCRUD";

import {
  Backdrop,
  CircularProgress,
  DialogContentText,
  makeStyles,
} from "@material-ui/core";

import { Confirm, SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../../utils/appUtils";
import {
  findCarrierMembership,
  getCarrierUserAcl,
  hasCarrierLogin,
} from "../../../../utils/carrierUserUtils";
import { UserStatus, UserType } from "../../../../utils/constants";
import { emailInviteHTML } from "./emailInviteHTML";
import { emailCarrierInviteHTML } from "./emailCarrierInviteHTML";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

/**
 * Whether the typed address already belongs to somebody, and what that means for
 * this invite.
 *
 * The membership asked about is this yard's entry for the chosen carrier, not
 * "any membership anywhere": a dispatcher working three yards holds three rows,
 * and reading the first one an unordered relation returns used to refuse the
 * invite outright — a company's people were uninvitable at every yard but the one
 * that first took them on. Multi-yard carriers are the normal case now, so the
 * question is only ever about the row this invite would write.
 *
 * @param {object} formData - the submitted form values
 * @param {object} membership - reloadYardId and carrierId this invite writes into
 * @returns {Promise<{message: string, carrier?: object}|null>} null when the
 *   address is free; `carrier` present when it holds an invitable carrier account
 */
const validateEmailUnicity = async (formData, membership) => {
  try {
    const {
      carrierUser: duplicateCarrier,
      customer: duplicateCustomer,
      yardUser: duplicateUser,
    } = await findExistingUserByEmail(formData.email);

    if (duplicateUser || duplicateCustomer) {
      return {
        message: `User with this email already exists.`,
      };
    } else if (duplicateCarrier) {
      const reloadYardCarrier = findCarrierMembership(
        duplicateCarrier.reloadYardCarriers?.items,
        membership,
      );
      return {
        message: `User with this email already works with this carrier at your transload.`,
        carrier: {
          ...duplicateCarrier,
          email: formData.email,
          // The membership this invite would write, so the caller can tell a
          // working one (nothing to do) from a retired one (revive) from none at
          // all (create).
          active: reloadYardCarrier?.active,
          firstName: formData.firstName,
        },
      };
    }
  } catch (error) {
    logError(
      "managerApp:carriers:ValidateSaveButton:validateEmailUnicity",
      error,
      false,
    );
  }
  return null;
};

export const ValidateCreateButton = (props) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [duplicateUser, setDuplicateUser] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const { carrier } = props;

  const handleValidateSave = async () => {
    let messages = [
      <>
        Once saved, you will not be able to change the Carrier or contact
        information. <br />
        Please confirm all information before saving. <br />
      </>,
    ];
    const formState = form.getState();
    const formData = formState.values;
    const isValid = !formState.invalid; // Form-level validation status. Submit to force field-level form validation.
    setIsSaving(true);

    if (isValid) {
      // if valid, check email uniqueness — for the membership this invite would
      // write, which is this yard's row for the carrier picked on the form.
      const isDuplicate = await validateEmailUnicity(formData, {
        reloadYardId: window.appUser?.reloadYardId,
        carrierId: carrier?.id,
      });

      if (isDuplicate) {
        if (isDuplicate.carrier?.active === "true" || !isDuplicate.carrier) {
          // Either the address is a yard staff member's or a customer contact's
          // (!carrier — one address is one account), or it already holds a
          // working membership in this very carrier entry.
          notify(isDuplicate.message, "error");
          setIsSaving(false);
          return; // Halt submission if duplicate found
        } else {
          // handle existing CARRIER account
          setDuplicateUser(isDuplicate.carrier);
          messages.push(
            <>
              <br />
              This person already has an account on transloads.co <br />
              Continue with invitation to join your transload?
            </>,
          );
        }
      }
    }

    if (!isValid) {
      setIsSaving(false);
      // Submit anyway: the save is blocked client-side while the form is
      // invalid, but submitting is what marks the untouched fields so each one
      // says what it still needs. Without it, a blank required field only
      // flashes the backdrop and reports nothing. The sibling create paths all
      // submit-to-surface-errors the same way.
      handleSave();
      return; // return to edit form
    }
    if (messages.length > 0) {
      // Open Confirm dialog.
      setMessage(
        <DialogContentText>
          {messages.map((msg, index) => (
            <React.Fragment key={index}>{msg}</React.Fragment>
          ))}
        </DialogContentText>,
      );
      setOpen(true);
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setIsSaving(false);
  };

  const handleDialogConfirm = async () => {
    setOpen(false);

    if (duplicateUser) {
      // handle existing CARRIER account
      await onSuccess({ data: duplicateUser, isDuplicate: true });
    } else {
      handleSave(); // submit and force field-level form validation whether valid or not.
    }
  };

  const handleSave = () => {
    props.handleSubmitWithRedirect(); // submit and force field-level form validation whether valid or not.
  };

  const transform = async (data) => {
    data.status = UserStatus.PENDING_REG;
    data.email = data.email.toLowerCase();

    let sanitized = sanitizeCarrierUser(data);
    return sanitized;
  };

  const onSuccess = async ({ data, isDuplicate }) => {
    try {
      const formData = form.getState().values;

      if (!carrier) {
        notify("Error: Carrier not found", "error");
        return;
      }

      // Invitees always join as dispatch: they are the only carrier users who
      // sign in to the portal (drivers have no Cognito account), so neither the
      // type nor the permissions are asked for on the form any more.
      const userYardCarrier = {
        acl: getCarrierUserAcl(UserType.CARRIER_DISPATCH),
        type: UserType.CARRIER_DISPATCH,
        notes: formData.notes,
        blockAccess: formData.blockAccess,
      };

      let emailContent;

      // An account that exists is not the same as an account that can be signed
      // into: deleting a carrier user's last membership deletes their login, and
      // the CarrierUser record stays behind either way. Sending the sign-in
      // invitation off the record alone points a purged user at a password that
      // no longer opens anything.
      const inviteFor = (user) =>
        (hasCarrierLogin(user) ? emailCarrierInviteHTML : emailInviteHTML)(
          user.firstName,
          user.email,
          window.appUser?.firstName,
          window.appUser?.reloadYard.name,
        );

      if (isDuplicate) {
        // This yard's row for the carrier on the form, not whichever row the
        // relation happened to return first — the person may work several yards.
        const existingYardCarrier = findCarrierMembership(
          data.reloadYardCarriers?.items,
          {
            reloadYardId: window.appUser?.reloadYardId,
            carrierId: carrier.id,
          },
        );
        if (existingYardCarrier?.active === "false") {
          // Re-activate existing inactive join record for this yard.
          // Clear `deleted` so a previously-deep-deleted user can be re-invited
          // (DeepDeleteButton sets deleted=true alongside active="false").
          const reloadYardCarrier = {
            ...existingYardCarrier,
            active: "true",
            deleted: false,
            carrierId: existingYardCarrier.carrierId,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            reloadYardId: window.appUser.reloadYardId,
            businessName: carrier.businessName,
            ...userYardCarrier,
          };
          await performUpdateUserReloadCarrier(reloadYardCarrier);
          emailContent = inviteFor(data);
        } else if (!existingYardCarrier) {
          // Carrier exists globally but has no prior association with this yard
          await performCreateUserReloadCarrier(data, carrier, userYardCarrier);
          emailContent = inviteFor(data);
        } else {
          // Already active at this yard — should have been caught in handleValidateSave
          notify("This user is already active at this location.", "error");
          return;
        }
      } else {
        // Brand-new carrier user
        await performCreateUserReloadCarrier(data, carrier, userYardCarrier);
        emailContent = emailInviteHTML(
          data.firstName,
          data.email,
          window.appUser?.firstName,
          window.appUser?.reloadYard.name,
        );
      }

      const response = await API.post("transREST", "/sendEmail", {
        body: {
          to: data.email,
          bcc: ["support@transloads.co"],
          subject: "Your transloads.co Invite Is Waiting",
          template: "emailTemplate.html",
          htmlContent: emailContent,
        },
      });

      if (response.error) {
        throw response.error;
      } else {
        notify(`User created successfully.`);
        redirect("/carrierUsers");
      }
    } catch (error) {
      logError(
        "managerApp:carrierUsers:ValidateCreateButton:onSuccess",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError(
      "managerApp:carrierUsers:ValidateCreateButton:onFailure",
      error,
      notify,
    );
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        {...props}
        submitOnEnter={false}
        undoable={false}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        title="Save Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
